<script lang="ts">
import GlpCoinSilver from '@/assets/images/glp-coin-silver.png'
import GlpCoin from '@/assets/images/glp-coin.png'
import { useCurrencyFormatter } from '@/composables/useCurrencyFormatter'
import type { DepositBonusOption } from '@/types'
import { DateTime } from 'luxon'
import { type PropType, defineComponent } from 'vue'

export default defineComponent({
  name: 'DepositOptionCard',
  props: {
    option: {
      type: Object as PropType<
        DepositBonusOption & {
          coinCount: number
          silverCoinCount: number
          displayAmount: number
          bonusPercent: number
        }
      >,
      required: true,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['select'],
  setup() {
    return useCurrencyFormatter()
  },
  data() {
    return {
      GlpCoin,
      GlpCoinSilver,
    }
  },
  computed: {
    expiry(): DateTime | null {
      const expiry = this.option?.promotion_details?.expires_at ?? null

      if (expiry) {
        return DateTime.fromISO(expiry)
      }

      return null
    },
  },
  methods: {
    handleClick() {
      this.$emit('select', this.option)
    },
  },
})
</script>

<template>
  <div
    class="flex flex-1 flex-col items-center justify-center gap-2 rounded-lg p-3 text-center hover:cursor-pointer relative"
    :class="{
      'border-2 border-primary bg-primary-lighter': isSelected,
      'border m-[1px] border-slate-300': !isSelected,
      'border-2 border-secondary bg-green-50':
        option.status === 'pending_reservation',
      'option-selected': isSelected,
    }"
    @click="handleClick"
  >
    <!-- Unlocked badge for pending reservations -->
    <span
      v-if="option.status === 'pending_reservation'"
      class="absolute -top-2 -right-2 bg-secondary text-black text-xs font-bold px-2 py-1 rounded-full shadow-sm"
    >
      Unlocked!
    </span>

    <!-- Amount -->
    <p class="text-2xl font-bold">
      {{ formatCurrencyFromPence(option.displayAmount) }}
    </p>

    <!-- Bonus percentage -->
    <p v-if="option.bonusPercent > 0" class="font-bold text-blue-800">
      +{{ $n(option.bonusPercent, 'percent') }} Credit
    </p>

    <!-- Coin display with gold coins first, silver bonus coins trailing -->
    <div
      v-if="option.coinCount > 0"
      class="relative h-8"
      :style="`width: ${32 + 16 * (option.coinCount - 1)}px;`"
    >
      <!-- Gold coins -->
      <img
        v-for="i in option.coinCount - option.silverCoinCount"
        :key="`gold-${i}`"
        :src="GlpCoin"
        alt="GLP Coin"
        class="absolute top-0 size-8"
        :style="`left: ${(i - 1) * 16}px; z-index: ${i};`"
      />
      <!-- Silver bonus coins trailing on the right -->
      <img
        v-for="i in option.silverCoinCount"
        :key="`silver-${i}`"
        :src="GlpCoinSilver"
        alt="Bonus Coin"
        class="absolute top-0 size-8"
        :style="`left: ${(option.coinCount - option.silverCoinCount + i - 1) * 16}px; z-index: ${option.coinCount - option.silverCoinCount + i};`"
      />
    </div>

    <!-- Countdown timer for promotions with expiry -->
    <div
      v-if="expiry && option.type === 'promotion'"
      class="flex items-center gap-1 mt-1 whitespace-nowrap"
    >
      <p class="text-xs text-gray-600 inline">Ends in</p>

      <AppCountdown
        :datetime="expiry"
        :show-all-units="true"
        time-class="text-xs text-gray-600 font-bold"
        unit-class="text-xs text-gray-600 font-bold"
        class="gap-1"
      />
    </div>
  </div>
</template>
